import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Check, X, Star, Flame, Zap } from "lucide-react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { notify } from "@/lib/toast";

// Module-level constants
const TRIAL_FEATURES = [
    "1 Smart QR Code",
    "Up to 50 scans / leads (lifetime)",
    "Live dashboard with analytics",
    "WhatsApp connect button",
];
const TRIAL_EXCLUDED = ["No CSV export", "No custom branding"];

const GROWTH_FEATURES = [
    "Up to 3 QR Codes",
    "500 leads / month",
    "Smart WhatsApp auto-fill messages",
    "Full analytics dashboard",
    "CSV / Excel export",
    "Download & share QR (PNG / SVG)",
];

const PRO_FEATURES = [
    "Unlimited QR Codes",
    "Unlimited leads",
    "Advanced analytics & time-series",
    "Custom WhatsApp message templates",
    "Remove Connexa branding",
    "Priority support",
];

const BUSINESS_FEATURES = [
    "Everything in Pro",
    "Team access (multiple users)",
    "Custom landing page branding",
    "API access (coming soon)",
    "Premium 24/7 support",
];

function getAccentRing(popular, accent) {
    if (popular) return "border-brand-500/40 glow-green";
    if (accent) return `border-${accent}-500/30`;
    return "";
}

function PlanCTA({ popular, onCta, cta }) {
    const cls = `w-full py-3 rounded-xl font-semibold text-sm ${
        popular ? "btn-primary text-white" : "glass hover:bg-white/10 transition-all"
    }`;
    if (onCta) {
        return <button onClick={onCta} className={cls}>{cta}</button>;
    }
    return <Link to="/register" className={`block text-center ${cls}`}>{cta}</Link>;
}

function Plan({ badge, badgeColor, title, subtitle, price, period, originalPrice, features, excluded = [], micro, cta, popular, accent, onCta }) {
    const accentRing = getAccentRing(popular, accent);
    return (
        <div className={`glass rounded-3xl p-7 card-hover relative flex flex-col ${accentRing}`}>
            {badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wide text-white whitespace-nowrap ${badgeColor}`}>
                    {badge}
                </div>
            )}
            <h3 className="font-display font-bold text-lg mb-1">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500 mb-4 leading-snug">{subtitle}</p>}
            <div className="mb-5">
                {originalPrice && <div className="text-xs text-gray-500 line-through mb-0.5">{originalPrice}</div>}
                <span className="text-4xl font-bold gradient-text">{price}</span>
                {period && <span className="text-sm text-gray-500 ml-1">{period}</span>}
            </div>

            <ul className="space-y-3 mb-5 text-sm text-gray-300 flex-1">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-brand-400 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                    </li>
                ))}
                {excluded.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-gray-500">
                        <X className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="line-through">{f}</span>
                    </li>
                ))}
            </ul>

            {micro && (
                <p className="text-[11px] text-gray-500 italic mb-4 leading-relaxed">💡 {micro}</p>
            )}

            <PlanCTA popular={popular} onCta={onCta} cta={cta} />
        </div>
    );
}

function getCashfree(mode) {
    if (typeof window === "undefined") return null;
    const Cf = window.Cashfree || window.cashfree;
    if (!Cf) return null;
    // v3 SDK exposes a global Cashfree constructor — instantiate in the mode our backend reports.
    try {
        return typeof Cf === "function" ? Cf({ mode: mode || "production" }) : Cf;
    } catch {
        return Cf;
    }
}

async function startCheckout(planId) {
    const { data } = await api.post("/payments/create-order", { plan_id: planId });
    const cf = getCashfree(data.mode);
    if (!cf) throw new Error("Cashfree checkout failed to load. Please refresh and try again.");
    cf.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_self",
    });
}

function usePlanCheckout() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [loadingPlan, setLoadingPlan] = useState(null);

    async function upgrade(planId) {
        if (!user) {
            notify("Please sign up or log in first", "info");
            nav("/register");
            return;
        }
        setLoadingPlan(planId);
        try {
            await startCheckout(planId);
        } catch (e) {
            notify(formatErr(e), "error");
            setLoadingPlan(null);
        }
    }
    return { loadingPlan, upgrade };
}

export default function Pricing() {
    const { loadingPlan, upgrade } = usePlanCheckout();
    return (
        <section className="min-h-screen pt-40 pb-16 mesh-gradient" data-testid="pricing-page">
            <div className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-10">
                    <span className="inline-block px-4 py-2 rounded-full glass-light text-xs font-semibold text-brand-400 mb-4">PRICING</span>
                    <h1 className="font-display font-bold text-4xl md:text-5xl mb-4 leading-tight">
                        Turn Every QR Scan into a <span className="text-whats-green">WhatsApp Lead</span> <span className="inline-block">🚀</span>
                    </h1>
                    <p className="text-gray-400 max-w-2xl mx-auto text-lg">
                        Start with a 7-day free trial. Upgrade anytime. Cancel anytime.
                    </p>
                </div>

                {/* Promo banner */}
                <div className="glass rounded-2xl p-5 md:p-6 mb-10 max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 glow-green border-brand-500/40" data-testid="promo-banner">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-2xl">🎁</div>
                        <div>
                            <div className="font-display font-bold text-lg">Launch Offer — 30 days <span className="gradient-text">FREE</span> on Growth plan</div>
                            <div className="text-sm text-gray-400">Use code at signup. No credit card required.</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="px-4 py-2 rounded-lg bg-black/40 border border-brand-500/40 font-mono font-bold text-brand-300 text-sm tracking-wider" data-testid="promo-code">CONNEXA03</div>
                        <button onClick={() => { navigator.clipboard.writeText("CONNEXA03"); notify("Code copied — use it at signup", "success"); }} className="text-xs text-gray-400 hover:text-brand-400">Copy</button>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Plan
                        title="Trial"
                        subtitle="7 days to see the magic — auto-applied at signup"
                        price="₹0"
                        period="/ 7 days"
                        features={TRIAL_FEATURES}
                        excluded={TRIAL_EXCLUDED}
                        cta="Start Free Trial"
                    />

                    <Plan
                        badge="⭐ MOST POPULAR"
                        badgeColor="bg-gradient-to-r from-brand-400 to-brand-600"
                        popular
                        title={<span className="flex items-center gap-1.5">Growth <Star className="w-4 h-4 text-yellow-400" /></span>}
                        subtitle="For serious users who want real leads"
                        price="₹199"
                        period="/month"
                        features={GROWTH_FEATURES}
                        micro="Perfect for shop owners, agents & small businesses"
                        cta={loadingPlan === "growth" ? "Redirecting..." : "Upgrade to Growth"}
                        onCta={() => upgrade("growth")}
                    />

                    <Plan
                        badge="🔥 BEST VALUE"
                        badgeColor="bg-gradient-to-r from-purple-500 to-pink-500"
                        accent="purple"
                        title={<span className="flex items-center gap-1.5">Pro <Flame className="w-4 h-4 text-orange-400" /></span>}
                        subtitle="Turn your QR into a full lead machine"
                        price="₹499"
                        period="/month"
                        features={PRO_FEATURES}
                        micro="Built for businesses that depend on daily leads"
                        cta={loadingPlan === "pro" ? "Redirecting..." : "Go Pro"}
                        onCta={() => upgrade("pro")}
                    />

                    <Plan
                        badge="🏢 BUSINESS"
                        badgeColor="bg-gradient-to-r from-yellow-500 to-orange-500"
                        accent="yellow"
                        title={<span className="flex items-center gap-1.5">Business+ <Zap className="w-4 h-4 text-yellow-400" /></span>}
                        subtitle="For teams & high-volume lead generation"
                        price="₹999"
                        period="/month"
                        features={BUSINESS_FEATURES}
                        cta={loadingPlan === "business" ? "Redirecting..." : "Upgrade to Business+"}
                        onCta={() => upgrade("business")}
                    />
                </div>

                <div className="mt-16 text-center">
                    <p className="text-gray-500 text-sm">All plans include WhatsApp integration · Cancel anytime · GST extra where applicable</p>
                </div>
            </div>
        </section>
    );
}
